import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PlusCircle, Trash, PencilSimple, Briefcase, Users, Clock, Fire, FilePdf, UsersThree } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import CandidatesModal from "@/components/hr/CandidatesModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = { open: "Abierta", screening: "Filtrado CV", interviewing: "Entrevistas", offered: "Oferta", hired: "Contratado", closed: "Cerrada", on_hold: "En pausa" };
const PRIORITIES = { low: "Baja", medium: "Media", high: "Alta", critical: "Crítica" };
const STATUS_COLOR = {
  open: "bg-emerald-100 text-emerald-800 border-emerald-200",
  screening: "bg-blue-100 text-blue-800 border-blue-200",
  interviewing: "bg-indigo-100 text-indigo-800 border-indigo-200",
  offered: "bg-purple-100 text-purple-800 border-purple-200",
  hired: "bg-slate-100 text-slate-500 border-slate-200",
  closed: "bg-slate-100 text-slate-400 border-slate-200",
  on_hold: "bg-amber-100 text-amber-800 border-amber-200",
};
const PRIORITY_COLOR = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-blue-100 text-blue-800",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};

const emptyVac = () => ({
  title: "", department: "", area: "", reports_to: "", headcount: 1, priority: "medium",
  status: "open", posted_date: "", target_start_date: "", contract_type: "planilla",
  regime: "14x7", salary_min: 0, salary_max: 0, currency: "MXN",
  location: "", remote: "on_site",
  summary: "", responsibilities: [], must_have: [], nice_to_have: [],
  experience_years: 0, education: "", languages: [], benefits: [],
  hiring_manager: "", recruiter: "", notes: "",
});

// Preserva espacios internos y leading/trailing; solo elimina líneas
// totalmente vacías. Antes usábamos `.trim()` en cada línea, lo que
// desconcertaba a usuarios que necesitaban indentar o dejar espacios
// intencionales.
const asList = (str) => str.split(/\r?\n/).filter((s) => s.trim() !== "");
const asMultiline = (arr) => (arr || []).join("\n");

/**
 * Textarea that stores its raw text locally so the user can type spaces and
 * newlines freely. On blur we push the parsed list (empty lines removed) to
 * the parent form.
 *
 * Fix 55p — El bug reportado era "no permite dar espacios ni saltar líneas".
 * Cause raíz: cuando el parent re-renderizaba por otro campo del formulario
 * y la referencia del array `value` cambiaba, `useEffect` re-hidrataba el
 * buffer local con el valor confirmado (post-trim). Ahora usamos un ref
 * `focusedRef` que bloquea la re-hidratación mientras el usuario tenga
 * el foco en el textarea → sus espacios y newlines quedan intactos hasta
 * confirmar el blur.
 */
function ListTextarea({ value, onCommit, rows = 4, placeholder, testid }) {
  const [text, setText] = useState(() => asMultiline(value));
  const focusedRef = useRef(false);
  const key = (value || []).join("|");
  useEffect(() => {
    if (!focusedRef.current) setText(asMultiline(value));
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Textarea
      rows={rows}
      value={text}
      data-testid={testid}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { focusedRef.current = false; onCommit(asList(text)); }}
      placeholder={placeholder}
    />
  );
}

export default function VacanciesTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVac());
  const [candidatesFor, setCandidatesFor] = useState(null);

  const load = useCallback(() => {
    const qs = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
    api.get(`/hr/vacancies${qs}`).then((r) => setItems(r.data)).catch(() => {});
    api.get("/hr/vacancies/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = {
        ...form,
        headcount: Number(form.headcount) || 1,
        salary_min: Number(form.salary_min) || 0,
        salary_max: Number(form.salary_max) || 0,
        experience_years: Number(form.experience_years) || 0,
      };
      if (editing) await api.put(`/hr/vacancies/${editing}`, payload);
      else await api.post("/hr/vacancies", payload);
      toast.success("Vacante guardada");
      setOpen(false); setEditing(null); setForm(emptyVac()); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar vacante?")) return;
    try { await api.delete(`/hr/vacancies/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const downloadBrief = async (v) => {
    try {
      const res = await api.get(`/hr/vacancies/${v.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vacante-${(v.title || v.id).replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const editOpen = (v) => {
    setEditing(v.id);
    setForm({ ...emptyVac(), ...v });
    setOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="hr-vacancies">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={<Briefcase size={16} weight="duotone" className="text-indigo-700" />} label="Vacantes" value={stats.total} testid="vac-kpi-total" />
          <Kpi icon={<Users size={16} weight="duotone" className="text-emerald-700" />} label="Plazas abiertas" value={stats.open_seats} testid="vac-kpi-seats" />
          <Kpi icon={<Fire size={16} weight="duotone" className="text-red-700" />} label="Prioridad alta/crítica" value={stats.by_priority.high + stats.by_priority.critical} testid="vac-kpi-hot" />
          <Kpi icon={<Clock size={16} weight="duotone" className="text-amber-700" />} label="Envejecidas >30d" value={stats.aging_over_30d} testid="vac-kpi-aging" color={stats.aging_over_30d > 0 ? "text-red-600" : "text-slate-900"} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-9" data-testid="vac-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyVac()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="vac-new" className="bg-blue-900 hover:bg-blue-800 gap-2 ml-auto"><PlusCircle size={14} weight="bold" /> Nueva vacante</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nueva"} vacante</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-500 italic mb-2">Complete un brief tipo &quot;job order&quot; como lo hacen las agencias top: misión, responsabilidades, must-have vs. nice-to-have.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="overline-label">Título del puesto *</Label>
                  <Input data-testid="vac-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Perforista Senior — Mina Subterránea" /></div>
                <div><Label className="overline-label">Departamento</Label><Input data-testid="vac-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Operaciones Mina" /></div>
                <div><Label className="overline-label">Área</Label><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Rampa 1200" /></div>
                <div><Label className="overline-label">Reporta a</Label><Input value={form.reports_to} onChange={(e) => setForm({ ...form, reports_to: e.target.value })} placeholder="Supervisor de mina" /></div>
                <div><Label className="overline-label">Ubicación</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Sonora, México" /></div>
                <div><Label className="overline-label">Modalidad</Label>
                  <Select value={form.remote} onValueChange={(v) => setForm({ ...form, remote: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="on_site">Presencial</SelectItem>
                      <SelectItem value="hybrid">Híbrido</SelectItem>
                      <SelectItem value="remote">Remoto</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Tipo de contrato</Label>
                  <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="planilla">Planilla</SelectItem>
                      <SelectItem value="contratista">Contratista</SelectItem>
                      <SelectItem value="temp">Temporal</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Régimen</Label>
                  <Select value={form.regime} onValueChange={(v) => setForm({ ...form, regime: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {["14x7", "10x4", "20x10", "5x2"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Prioridad</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="vac-priority"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(PRIORITIES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Estado</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="vac-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Plazas</Label><Input type="number" min={1} value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} /></div>
                <div><Label className="overline-label">Años exp. mínimos</Label><Input type="number" min={0} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} /></div>
                <div><Label className="overline-label">Salario mín.</Label><Input type="number" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} /></div>
                <div><Label className="overline-label">Salario máx.</Label><Input type="number" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} /></div>
                <div><Label className="overline-label">Publicada</Label><Input type="date" value={form.posted_date} onChange={(e) => setForm({ ...form, posted_date: e.target.value })} /></div>
                <div><Label className="overline-label">Inicio objetivo</Label><Input type="date" value={form.target_start_date} onChange={(e) => setForm({ ...form, target_start_date: e.target.value })} /></div>
                <div><Label className="overline-label">Hiring Manager</Label><Input value={form.hiring_manager} onChange={(e) => setForm({ ...form, hiring_manager: e.target.value })} /></div>
                <div><Label className="overline-label">Reclutadora</Label><Input value={form.recruiter} onChange={(e) => setForm({ ...form, recruiter: e.target.value })} /></div>
                <div className="col-span-2 border-t pt-2 mt-1">
                  <Label className="overline-label">Misión / summary</Label>
                  <Textarea rows={2} data-testid="vac-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="En 2 líneas: para qué existe este puesto y qué impacto tendrá." />
                </div>
                <div><Label className="overline-label">Responsabilidades (1 por línea)</Label>
                  <ListTextarea value={form.responsibilities} onCommit={(v) => setForm({ ...form, responsibilities: v })} rows={4}
                                 placeholder="Operar jumbo perforador&#10;Cumplir plan diario&#10;Reportar incidentes" /></div>
                <div><Label className="overline-label">Educación / idiomas</Label>
                  <Input value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} placeholder="Bachiller / Técnico / Ingeniero" />
                  <div className="mt-1">
                    <ListTextarea value={form.languages} onCommit={(v) => setForm({ ...form, languages: v })} rows={2}
                                   placeholder="Español (nativo)&#10;Inglés (intermedio)" />
                  </div></div>
                <div><Label className="overline-label">Must-have (1 por línea)</Label>
                  <ListTextarea value={form.must_have} onCommit={(v) => setForm({ ...form, must_have: v })} rows={4} testid="vac-musthave"
                                 placeholder="Curso de altura vigente&#10;5+ años en mina subterránea&#10;Licencia interna" /></div>
                <div><Label className="overline-label">Nice-to-have (1 por línea)</Label>
                  <ListTextarea value={form.nice_to_have} onCommit={(v) => setForm({ ...form, nice_to_have: v })} rows={4}
                                 placeholder="Certificado LOTO&#10;Experiencia en Fann&#10;Bilingüe" /></div>
                <div className="col-span-2"><Label className="overline-label">Beneficios (1 por línea)</Label>
                  <ListTextarea value={form.benefits} onCommit={(v) => setForm({ ...form, benefits: v })} rows={2}
                                 placeholder="EPS 100% cubierta&#10;Bono de producción&#10;Escalera de carrera" /></div>
                <div className="col-span-2"><Label className="overline-label">Notas internas</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Comentarios internos de reclutamiento…" /></div>
              </div>
              <Button data-testid="vac-save" onClick={save} disabled={!form.title} className="bg-blue-900 hover:bg-blue-800 w-full mt-2">
                {editing ? "Actualizar vacante" : "Publicar vacante"}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">Sin vacantes registradas</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="vac-list">
            {items.map((v) => (
              <li key={v.id} data-testid={`vac-row-${v.id}`} className="p-4 flex items-start gap-4 hover:bg-slate-50 transition">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 truncate">{v.title}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_COLOR[v.status] || ""}`}>{STATUSES[v.status] || v.status}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${PRIORITY_COLOR[v.priority] || ""}`}>{PRIORITIES[v.priority] || v.priority}</span>
                    {v.headcount > 1 && <span className="text-xs text-slate-500">×{v.headcount} plazas</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {v.department} · {v.area} · {v.location || "-"} · {v.regime} · {v.remote}
                  </p>
                  {v.summary && <p className="text-sm text-slate-700 mt-2 leading-snug">{v.summary}</p>}
                  {v.must_have?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {v.must_have.slice(0, 5).map((m, i) => (
                        <span key={i} className="text-[10px] bg-blue-50 text-blue-900 border border-blue-100 px-2 py-0.5 rounded">{m}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-2">
                    Publicada {v.posted_date} · Objetivo {v.target_start_date || "—"} · {v.recruiter || "sin reclutadora"}
                  </p>
                </div>
                {canManage && (
                  <div className="flex flex-col gap-1">
                    <button data-testid={`vac-candidates-${v.id}`} onClick={() => setCandidatesFor(v)} title="Ver candidatos" className="text-slate-400 hover:text-indigo-700 transition p-1"><UsersThree size={16} /></button>
                    <button data-testid={`vac-pdf-${v.id}`} onClick={() => downloadBrief(v)} title="Exportar brief PDF" className="text-slate-400 hover:text-red-700 transition p-1"><FilePdf size={16} /></button>
                    <button data-testid={`vac-edit-${v.id}`} onClick={() => editOpen(v)} className="text-slate-400 hover:text-blue-900 transition p-1"><PencilSimple size={16} /></button>
                    <button data-testid={`vac-delete-${v.id}`} onClick={() => remove(v.id)} className="text-slate-400 hover:text-red-500 transition p-1"><Trash size={16} /></button>
                  </div>
                )}
                {!canManage && (
                  <div className="flex flex-col gap-1">
                    <button data-testid={`vac-candidates-${v.id}`} onClick={() => setCandidatesFor(v)} title="Ver candidatos" className="text-slate-400 hover:text-indigo-700 transition p-1"><UsersThree size={16} /></button>
                    <button data-testid={`vac-pdf-${v.id}`} onClick={() => downloadBrief(v)} title="Exportar brief PDF" className="text-slate-400 hover:text-red-700 transition p-1"><FilePdf size={16} /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <CandidatesModal
        vacancyId={candidatesFor?.id}
        vacancyTitle={candidatesFor?.title}
        canManage={canManage}
        open={Boolean(candidatesFor)}
        onOpenChange={(o) => { if (!o) setCandidatesFor(null); }}
      />
    </div>
  );
}

function Kpi({ icon, label, value, testid, color = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">{icon}<p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</p></div>
      <p className={`font-heading font-black text-3xl tracking-tight ${color}`}>{value}</p>
    </div>
  );
}
